import { NavLink } from 'react-router-dom';
import { NAV } from '@/app/routeConfig';
import { BrandMark } from './BrandMark';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';
import { useRole } from '@/hooks/useRole';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/hooks/useAuth';
import { ROLE_LABEL } from '@/types/domain';

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useRole();
  return (
    <nav className="flex flex-col gap-6">
      {NAV.map((section, idx) => {
        const items = section.items.filter((i) => !i.capability || can(i.capability));
        if (items.length === 0) return null;
        return (
          <div key={section.heading ?? `top-${idx}`} className="flex flex-col gap-1">
            {section.heading && (
              <p className="px-3 pb-1 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-subtle">
                {section.heading}
              </p>
            )}
            {items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/app'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-brand/10 font-medium text-brand ring-1 ring-inset ring-brand/25'
                      : 'text-muted hover:bg-elevated hover:text-content',
                  )
                }
              >
                <Icon name={item.icon} size={18} />
                {item.label}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );
}

export function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const user = useCurrentUser();
  const { signOut } = useAuth();

  return (
    <div className="flex h-full flex-col gap-6 p-4">
      <div className="px-2 pt-2">
        <BrandMark />
      </div>

      <div className="flex-1 overflow-y-auto pr-1">
        <NavItems onNavigate={onNavigate} />
      </div>

      <div className="rounded-card border border-brand/20 bg-gradient-to-b from-brand/10 to-transparent p-4 text-center">
        <div className="mx-auto mb-2 flex h-8 w-8 items-center justify-center text-brand">
          <Icon name="crown" size={22} />
        </div>
        <p className="text-sm font-semibold text-content">Premium Club</p>
        <p className="mt-0.5 text-xs text-muted">Elite experience. Exceptional service.</p>
      </div>

      {user && (
        <div className="flex items-center gap-3 border-t border-line pt-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-elevated text-xs font-semibold text-brand ring-1 ring-brand/30">
            {user.initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-content">{user.name}</p>
            <p className="truncate text-xs text-muted">{ROLE_LABEL[user.role]}</p>
          </div>
        </div>
      )}

      <button
        onClick={signOut}
        className="focus-ring flex items-center justify-between rounded-lg px-3 py-2 text-sm text-muted hover:bg-elevated hover:text-content"
      >
        <span className="flex items-center gap-3">
          <Icon name="signout" size={18} />
          Sign out
        </span>
        <Icon name="chevronRight" size={16} />
      </button>
    </div>
  );
}

export function Sidebar() {
  return (
    <aside className="hidden w-[260px] shrink-0 border-r border-brand/10 bg-surface/40 backdrop-blur-xl lg:block">
      <div className="sticky top-0 h-screen">
        <SidebarContent />
      </div>
    </aside>
  );
}
